import { FileSpreadsheet, FolderOpen, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

import { GooglePicker } from './google-picker';

import type { ResourcePickerConfig, SelectedResource } from '@optidev/shared-types';

interface ResourcePickerProps {
  config: ResourcePickerConfig;
  value: SelectedResource[];
  onChange: (resources: SelectedResource[]) => void;
  accessToken?: string;
  connectionId?: string;  // For lazy token fetching
  getAuthToken?: () => Promise<string | null>;  // For authenticated API calls
  disabled?: boolean;
  error?: string;
  label?: string;
  required?: boolean;
  description?: string;
}

/**
 * ResourcePicker - Universal file/resource picker component
 *
 * Supports:
 * - Google Picker API (overlay)
 */
export function ResourcePicker({
  config,
  value = [],
  onChange,
  accessToken,
  connectionId,
  getAuthToken,
  disabled,
  error,
  label,
  required,
  description,
}: ResourcePickerProps) {
  const removeResource = (id: string) => {
    onChange(value.filter((r) => r.id !== id));
  };

  const handleSelect = (resources: SelectedResource[]) => {
    // Merge with existing, avoiding duplicates
    const merged = [...value];
    for (const res of resources) {
      if (!merged.some((r) => r.id === res.id)) {
        merged.push(res);
      }
    }
    // Respect maxSelection
    const limited = config.maxSelection ? merged.slice(0, config.maxSelection) : merged;
    onChange(limited);
  };

  const canAddMore = !config.maxSelection || value.length < config.maxSelection;

  return (
    <div className="space-y-3">
      {/* Label */}
      {label && (
        <Label className="flex items-center gap-1">
          {label}
          {required && <span className="text-destructive">*</span>}
        </Label>
      )}

      {/* Selected resources */}
      {value.length > 0 && (
        <div className="space-y-2">
          {value.map((resource) => (
            <div
              key={resource.id}
              className="flex items-center justify-between p-3 border rounded-md bg-muted/50"
            >
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                <div>
                  <span className="text-sm font-medium">{resource.name}</span>
                  {resource.url && (
                    <a
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-xs text-muted-foreground hover:underline truncate max-w-[300px]"
                    >
                      {resource.url}
                    </a>
                  )}
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeResource(resource.id)}
                disabled={disabled}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Picker button */}
      {canAddMore && (
        <>
          {config.provider === 'google' && (
            <GooglePicker
              config={config}
              accessToken={accessToken}
              connectionId={connectionId}
              getAuthToken={getAuthToken}
              onSelect={handleSelect}
              disabled={disabled || (!accessToken && !connectionId)}
              buttonLabel={value.length > 0 ? 'Add More' : 'Select Spreadsheet'}
              currentSelectionCount={value.length}
            />
          )}

          {!config.provider && (
            <Button type="button" variant="outline" className="w-full" disabled>
              <FolderOpen className="h-4 w-4 mr-2" />
              Unsupported picker provider
            </Button>
          )}
        </>
      )}

      {/* No token message - only show if we can't fetch lazily either */}
      {!accessToken && !connectionId && (
        <p className="text-sm text-muted-foreground">
          Complete OAuth authorization first to select files
        </p>
      )}

      {/* Description */}
      {description && <p className="text-xs text-muted-foreground">{description}</p>}

      {/* Error */}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export default ResourcePicker;
